import { useState, useEffect } from "react";
import api from "../lib/api";
import { Gift, Plus, Search, CreditCard, Copy } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";

export default function GiftCardsPage() {
  const [giftCards, setGiftCards] = useState([]);
  const [packages, setPackages] = useState([]);
  const [clientPackages, setClientPackages] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewGC, setShowNewGC] = useState(false);
  const [showNewPkg, setShowNewPkg] = useState(false);
  const [newGC, setNewGC] = useState({ amount: 50, purchaser_name: "", recipient_name: "", purchaser_email: "", message: "" });
  const [newPkg, setNewPkg] = useState({ name: "", price: 0, description: "" });

  useEffect(() => {
    const load = async () => {
      try {
        const [gcRes, pkgRes, cpRes, cliRes] = await Promise.all([
          api.get("/gift-cards"), api.get("/packages"), api.get("/client-packages"), api.get("/clients?limit=200")
        ]);
        setGiftCards(gcRes.data); setPackages(pkgRes.data);
        setClientPackages(cpRes.data); setClients(cliRes.data.clients || []);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const createGiftCard = async () => {
    try {
      await api.post("/gift-cards", newGC);
      toast.success("Gift card created!");
      setShowNewGC(false);
      const res = await api.get("/gift-cards"); setGiftCards(res.data);
    } catch (err) { toast.error("Failed"); }
  };

  const totalOutstanding = giftCards.filter(gc => gc.status === "active").reduce((s, gc) => s + gc.balance, 0);

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="giftcards-page">
      <div className="flex items-center justify-between mb-8 animate-fadeIn">
        <div>
          <h1 className="font-['Outfit'] text-3xl font-light tracking-tight text-slate-900">Gift Cards & Packages</h1>
          <p className="text-slate-600 text-sm mt-1">Manage gift cards and service packages</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="new-giftcard-btn" onClick={() => setShowNewGC(true)} className="bg-slate-900 text-white hover:bg-slate-800 rounded-full h-10 px-5 text-sm">
            <Gift className="w-4 h-4 mr-1.5" /> New Gift Card
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-2"><Gift className="w-4 h-4 text-slate-400" strokeWidth={1.5} /><span className="text-xs font-bold uppercase tracking-widest text-slate-400">Active Cards</span></div>
          <p className="font-['Outfit'] text-2xl font-medium text-slate-900">{giftCards.filter(g => g.status === "active").length}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-2"><CreditCard className="w-4 h-4 text-slate-400" strokeWidth={1.5} /><span className="text-xs font-bold uppercase tracking-widest text-slate-400">Outstanding Balance</span></div>
          <p className="font-['Outfit'] text-2xl font-medium text-slate-900">${totalOutstanding.toFixed(0)}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-2"><CreditCard className="w-4 h-4 text-slate-400" strokeWidth={1.5} /><span className="text-xs font-bold uppercase tracking-widest text-slate-400">Packages Sold</span></div>
          <p className="font-['Outfit'] text-2xl font-medium text-slate-900">{clientPackages.length}</p>
        </div>
      </div>

      <Tabs defaultValue="giftcards">
        <TabsList className="bg-[#FAFAFA] p-1 rounded-lg border border-slate-200 mb-4">
          <TabsTrigger value="giftcards" className="text-sm">Gift Cards</TabsTrigger>
          <TabsTrigger value="packages" className="text-sm">Packages</TabsTrigger>
        </TabsList>

        <TabsContent value="giftcards">
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <table className="w-full">
              <thead><tr className="bg-[#FAFAFA] border-b border-slate-200">
                <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Code</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Recipient</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Status</th>
                <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Original</th>
                <th className="text-right px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Balance</th>
              </tr></thead>
              <tbody>
                {giftCards.map(gc => (
                  <tr key={gc.gift_card_id} className="border-b border-slate-200 hover:bg-[#FAFAFA]" data-testid={`gc-${gc.gift_card_id}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-medium text-slate-900">{gc.code}</span>
                        <button onClick={() => { navigator.clipboard.writeText(gc.code); toast.success("Copied!"); }} className="text-slate-400 hover:text-slate-900">
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5">From: {gc.purchaser_name}</p>
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-600">{gc.recipient_name || "—"}</td>
                    <td className="px-4 py-4">
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${gc.status === "active" ? "bg-[#E8F0E9] text-emerald-600" : "bg-[#FAFAFA] text-slate-400"}`}>{gc.status}</span>
                    </td>
                    <td className="px-4 py-4 text-sm text-right text-slate-400">${gc.amount}</td>
                    <td className="px-6 py-4 text-sm text-right font-medium text-slate-900">${gc.balance?.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="packages">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {packages.map(pkg => (
              <div key={pkg.package_id} className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-md transition-shadow" data-testid={`pkg-${pkg.package_id}`}>
                <h3 className="font-['Outfit'] text-lg font-medium text-slate-900 mb-1">{pkg.name}</h3>
                <p className="text-sm text-slate-600 mb-3">{pkg.description}</p>
                <p className="font-['Outfit'] text-2xl font-medium text-slate-900 mb-2">${pkg.price}</p>
                <p className="text-xs text-slate-400">Valid for {pkg.expiry_days} days</p>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={showNewGC} onOpenChange={setShowNewGC}>
        <DialogContent className="sm:max-w-[440px] bg-white border-slate-200 rounded-2xl p-8">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-xl font-medium">New Gift Card</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="flex gap-2">
              {[25, 50, 75, 100, 150].map(a => (
                <button key={a} onClick={() => setNewGC(p => ({...p, amount: a}))} className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors ${newGC.amount === a ? 'bg-slate-900 text-white' : 'bg-[#FAFAFA] text-slate-600 border border-slate-200'}`}>${a}</button>
              ))}
            </div>
            <Input data-testid="gc-purchaser" value={newGC.purchaser_name} onChange={e => setNewGC(p => ({...p, purchaser_name: e.target.value}))} placeholder="Purchaser name" className="h-11 border-slate-200 rounded-lg" />
            <Input value={newGC.recipient_name} onChange={e => setNewGC(p => ({...p, recipient_name: e.target.value}))} placeholder="Recipient name" className="h-11 border-slate-200 rounded-lg" />
            <Input value={newGC.message} onChange={e => setNewGC(p => ({...p, message: e.target.value}))} placeholder="Personal message (optional)" className="h-11 border-slate-200 rounded-lg" />
            <Button data-testid="create-gc-btn" onClick={createGiftCard} className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-full h-11">Create Gift Card (${newGC.amount})</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
